import React, { useRef, useState, useCallback } from "react";
import { PortableText } from "@portabletext/react";
import { Link, useNavigate } from "react-router-dom";
import { urlFor } from "../sanityClient";
import CheckoutButton from "./CheckoutButton";
import { useCartStore } from "../stores/cartStore";
import "./ProductCard.css";

function ProductCard({ product, view = "card" }) {
  const { addToCart } = useCartStore();
  const imgRef = useRef(null);
  const navigate = useNavigate();
 
  const [pricing, setPricing] = useState({
    price: product && typeof product.price === "number" ? product.price : null,
    currency: (product && product.currency) || "CAD",
    stripePriceId: (product && product.stripePriceId) || null,
  });
  const [pricingLoading, setPricingLoading] = useState(false);

  const resolvedSlug =
    typeof product?.slug === "string"
      ? product.slug
      : product?.slug?.current ?? "";

  // 1. Normalize the data (Now including 'details' and 'inventory')
  const normalized = {
    _id: product?._id,
    title: product?.title ?? product?.name ?? "Untitled",
    slug: resolvedSlug,
    price:
      typeof product?.price === "number" ? product.price : pricing.price ?? null,
    currency: pricing.currency ?? product?.currency ?? "CAD",
    stripePriceId: pricing.stripePriceId ?? product?.stripePriceId ?? null,
    mainImage: product?.mainImage ?? product?.images?.[0] ?? null,
    description: product?.description ?? null,
    // Capture real data from Phase A
    inventory: typeof product?.inventory === "number" ? product.inventory : null, 
    details: product?.details || {}, 
  };

  const mainImageUrl = normalized.mainImage
    ? urlFor(normalized.mainImage).width(800).auto("format").url()
    : null;

  // Logic: It's sold out ONLY if inventory is explicitly 0. 
  // Null/Undefined inventory counts as "Unlimited" (In Stock).
  const isSoldOut = normalized.inventory !== null && normalized.inventory <= 0;

  const fetchPricing = useCallback(async () => {
    if (pricing.price != null && pricing.currency && pricing.stripePriceId) {
      return;
    }
    if (!normalized._id) return;

    try {
      setPricingLoading(true);
      const res = await fetch(
        `/.netlify/functions/get-product-pricing?productId=${encodeURIComponent(
          normalized._id
        )}`,
        { credentials: "same-origin" }
      );
      const data = await res.json().catch(() => ({}));
      setPricing((prev) => ({
        price:
          typeof data.price === "number"
            ? data.price
            : prev.price ?? normalized.price ?? null,
        currency: data.currency ?? prev.currency ?? normalized.currency ?? "CAD",
        stripePriceId:
          data.stripePriceId ??
          prev.stripePriceId ??
          normalized.stripePriceId ??
          null,
      }));
    } catch (err) {
      console.error("fetchPricing error:", err);
    } finally {
      setPricingLoading(false);
    }
  }, [
    normalized._id,
    normalized.price,
    normalized.currency,
    normalized.stripePriceId,
    pricing.price,
    pricing.currency,
    pricing.stripePriceId,
  ]);

  const onAddToCart = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (pricing.price == null) await fetchPricing();

    const enriched = {
      ...product,
      price: (pricing.price ?? product?.price) ?? null,
      currency: (pricing.currency ?? product?.currency) ?? "CAD",
      stripePriceId:
        (pricing.stripePriceId ?? product?.stripePriceId) ?? null,
    };

    addToCart(enriched);
  };

  const onViewDetails = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (normalized.slug) navigate(`/store/${normalized.slug}`);
  };

  if (!product) return null;

  // Helper for dynamic format text
  const formatText = normalized.details.format || "Standard";

  return (
    <div className={`product-card-wrapper ${view}`}>
      <div className={`product-card ${view}`} onClick={view === 'card' ? onViewDetails : undefined}>
        
        {/* FRONT FACE */}
        <div className="box-face box-front">
          <div className="product-image-wrap">
            {mainImageUrl ? (
              <img
                ref={imgRef}
                src={mainImageUrl}
                alt={normalized.title}
                className="product-image"
                loading="lazy"
              />
            ) : (
              <div className="product-image-fallback">No Image</div>
            )}
            {isSoldOut && <div className="sold-out">Sold Out</div>}
          </div>

          <div className="product-body">
            <h2 className="product-title">
              {view === "card" ? (
                <Link className="product-title-link" to={`/store/${normalized.slug}`}>
                  {normalized.title}
                </Link>
              ) : (
                normalized.title
              )}
            </h2>

            {/* EXPANDED Nutrition Facts Panel */}
            <div className="nutrition-facts-panel">
               
               {/* Row 1: Format (Dynamic) */}
               {view === "card" && (
                 <div className="fact-row small-fact">
                   <span className="fact-label">Format</span>
                   <span className="fact-dots"></span>
                   <span className="fact-value">{formatText}</span>
                 </div>
               )}

               {/* Row 2: Price */}
               <div className="fact-row main-fact">
                 <span className="fact-label">Price</span>
                 <span className="fact-dots"></span>
                 <span className="fact-value">
                   {normalized.price != null ? `$${Number(normalized.price).toFixed(2)}` : '---'}
                 </span>
               </div>

               {/* Row 3: Availability (Dynamic Logic) */}
               {view === "card" && !isSoldOut && (
                 <div className="fact-row small-fact">
                   <span className="fact-label">Status</span>
                   <span className="fact-dots"></span>
                   <span className="fact-value status-green">In Stock</span>
                 </div>
               )}

               <div className="fact-divider"></div>
               
               {/* Action Button */}
               {view === "card" && !isSoldOut && (
                 <button 
                    className="btn-action-block" 
                    onClick={onAddToCart}
                    disabled={pricingLoading}
                 >
                   {pricingLoading ? "Adding..." : "Add to Cart"}
                 </button>
               )}
               {view === "card" && isSoldOut && (
                 <div className="btn-action-block disabled">Sold Out</div>
               )}
            </div>

            {view === "detail" && normalized.description && (
              <div className="product-description">
                <PortableText value={normalized.description} />
              </div>
            )}
          </div>
        </div>

        {/* HIDDEN SIDES (Flat Design) */}
        {view === 'card' && (
          <>
            <div className="box-face box-right"></div>
            <div className="box-face box-bottom"></div>
            
            {/* VIEW DETAILS STICKER */}
            <div className="floating-prize-container">
               <button
                  type="button"
                  className="btn-prize-sticker"
                  onClick={onViewDetails}
                  aria-label="View Details"
                >
                  View<br/>Details
                </button>
            </div>
          </>
        )}

        {/* DETAIL VIEW ACTIONS */}
        {view === 'detail' && (
          <div className="product-actions-detail">
            {!isSoldOut ? (
               <button
                  type="button"
                  className="btn btn-primary-alt"
                  onClick={onAddToCart}
                  disabled={pricingLoading}
                >
                  {pricingLoading ? "Adding..." : "Add to Cart"}
                </button>
            ) : (
               <div className="buy-unavailable">Unavailable</div>
            )}
            {normalized._id && <CheckoutButton productId={normalized._id} />}
          </div>
        )}

      </div>
      
      {view === 'card' && <div className="box-shadow"></div>}
    </div>
  );
}

export default ProductCard;